/**
 * The seam the HTTP client consults before it reaches the network.
 *
 * It is deliberately a bare holder with no imports of its own. The client needs to ask "is there
 * an offline layer, and does it want this request?" without depending on the offline layer, and
 * the offline layer needs to send requests through the client — so one of the two directions has
 * to go through something this small.
 *
 * With nothing installed, every method returns `null` and the client behaves exactly as it did
 * before any of this existed.
 */

export interface OfflineRequestRuntime {
  /**
   * Wraps a read. Returning `null` means the path is not part of the offline copy and the client
   * should simply go to the network.
   */
  interceptRead<T>(path: string, send: () => Promise<T>): Promise<T> | null;

  /**
   * Offers a write to the queue. Returning `null` means the client should send it now — either
   * because there is a connection, or because this write is not one that may be replayed later.
   */
  interceptWrite<T>(method: string, path: string, body: unknown): Promise<T> | null;
}

let runtime: OfflineRequestRuntime | null = null;

export function setOfflineRequestRuntime(next: OfflineRequestRuntime | null): void {
  runtime = next;
}

export function offlineRequestRuntime(): OfflineRequestRuntime | null {
  return runtime;
}
